import type { EntityData } from '@adornis/baseql/entities/types.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { GermanInsuranceTypes } from '../../../../db/enums/misc-enums.js';
import { HealthInsurance } from '../../../../healthinsurance/entities/HealthInsurance.js';
import type { Person } from '../../../entities/person.js';
import { ANPrivatVersicherung } from '../../entities/employee-sections/an-privatversicherung.js';

export const EmployeeSectionPrivatVersicherungForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  if (!props.form.document) return nothing;

  // if the section is not there and we should fill it
  if (
    props.form.document.mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus === GermanInsuranceTypes.PRIVAT &&
    !props.form.document.mitarbeiterDaten.anPrivatVersicherung
  ) {
    props.form.document.mitarbeiterDaten.anPrivatVersicherung = new ANPrivatVersicherung({});
  }

  if (props.form.document.mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus !== GermanInsuranceTypes.PRIVAT)
    return html`
      <div class="flex flex-col gap-2">
        <p class="font-semibold">Keine Angaben benötigt</p>
        <p class="text-base-content/70">( Keine Privatversicherung angegeben )</p>
      </div>
    `;

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Privatversicherung am 31.12.2002?',
        render: ToggleInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'istPrivatVersichert'),
          label: props.form.document.mitarbeiterDaten?.anPrivatVersicherung?.istPrivatVersichert
            ? 'privat versichert am 31.12.2002'
            : 'nicht privat versichert am 31.12.2002',
        }),
      })}
      ${FormGridEntry({
        label: 'Transitkrankenkasse',
        labelSubtext: 'Letzte gesetzliche Krankenkasse als privat versicherte Person',
        render: EntityPickerInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'letzteGesKrankenkasseID'),
          class: HealthInsurance,
          filter: new AdornisFilter({
            searchFields: ['bezeichnung'],
            sortingFields: ['bezeichnung'],
            sortingDirections: ['desc'],
          }),
          selectionSet: { bezeichnung: 1, ort: 1 },
          renderString: (item: HealthInsurance) => item.bezeichnung ?? '',
          placeholder: 'Krankenkasse nicht hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Priv. Krankenversicherungsbeitrag',
        labelSubtext: '§257 SGB V',
        render: NumberInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'krankenBeitrag'),
          suffix: '€',
          placeholder: 'Kein privater Krankenversicherungsbeitrag hinterlegt',
          placeholderMode: 'hide',
        }),
      })}
      ${FormGridEntry({
        label: 'Priv. Pflegeversicherungsbeitrag',
        labelSubtext: '§61 SGB XI',
        render: NumberInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'pflegenBeitrag'),
          suffix: '€',
          placeholder: 'Kein privater Pflegeversicherungsbeitrag hinterlegt',
          placeholderMode: 'hide',
        }),
      })}
      ${FormGridEntry({
        label: 'Anspruch auf Krankentagegeld',
        render: ToggleInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'anspruchKrankentageGeld'),
        }),
      })}
      ${FormGridEntry({
        label: 'Arbeitgeberzuschuss',
        labelSubtext: 'Bescheinigung nach §257 SGB IV und §61 SGB XI',
        render: FileInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'arbeitgeberzuschussID'),
          placeholder: 'Keine Kopie der Bescheinigungen zur Erlangung des Arbeitgeberzuschusses hinterlegt',
          placeholderMode: 'hide',
        }),
      })}
      ${FormGridEntry({
        label: 'Privat pflegeversichert',
        render: ToggleInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'isPrivatPflegeversichert'),
        }),
      })}
      ${FormGridEntry({
        label: 'Monatl. Beitragsanteil',
        labelSubtext: 'Basis-Krankenversicherungsschutz',
        render: NumberInput({
          ...props.form.field('mitarbeiterDaten', 'anPrivatVersicherung', 'beitragsanteilBasisKrankenversicherung'),
          placeholder: 'Kein Monatl. Beitragsanteil für Basis-Krankenversicherungsschutz hinterlegt',
          placeholderMode: 'hide',
          suffix: '€',
        }),
      })}
    </div>
  `;
});

export const EmployeeSectionPrivatVersicherungFormValidator = {
  istPrivatVersichert: (options: ValidationFunctionOptions<string, ANPrivatVersicherung>) => {
    if (options.source?.mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus === GermanInsuranceTypes.PRIVAT) {
      return sgNonOptional({ allowFalsy: true })(options);
    }
  },
};
